/**
 * Strips HTML tags from a string and decodes common HTML entities, returning
 * plain text suitable for rendering in a React Native `<Text>` component.
 *
 * Reference abstracts/snippets returned from upstream sources occasionally
 * contain inline HTML (e.g. `<p>`, `<sup>`, `<i>`, `<b>`) which leaks through
 * as raw markup when rendered as text. This helper removes the tags while
 * preserving the underlying text content and collapsing surrounding
 * whitespace.
 */

const NAMED_HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': ' ',
};

/** Decodes named, hex, and numeric HTML entities. Shared by any helper that extracts plain text from HTML. */
export function decodeHtmlEntities(input: string): string {
  return input
    .replace(/&(amp|lt|gt|quot|apos|nbsp);/g, (match) => NAMED_HTML_ENTITIES[match] ?? match)
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(parseInt(code, 10)));
}

export function stripHtml(input: string | null | undefined): string {
  if (!input) {
    return '';
  }

  return decodeHtmlEntities(
    input
      // Replace line-break tags with a space first so adjacent runs of
      // text don't get concatenated when the tag itself disappears. The
      // surrounding whitespace-collapse step at the end folds any extra
      // whitespace this introduces.
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<[^>]*>/g, '')
  )
    .replace(/\s+/g, ' ')
    .trim();
}
